import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Shield,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Target,
  Banknote,
  Zap,
  Car,
  Bus,
  Info,
  XCircle,
  Users,
  Heart,
  Home,
  ChevronDown,
  ChevronUp,
  Calendar,
  Mail,
  Building,
  ShoppingCart,
  Search,
  Filter,
  User,
  Phone,
  CreditCard,
} from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../../../components/misc/Loader";
import ApprovalModal from "./components/ApprovalModal";
import RejectionModal from "./components/RejectionModal";
import insurancePolicyService from "../../../api/InsurancePolicyService";

export default function PolicyDetailView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { policy_slug } = useParams();
  
  // Get provider slug from location state to preserve filter
  const providerSlug = location.state?.providerSlug;
  
  // Handle back navigation - preserve provider filter if it exists
  const handleBack = () => {
    if (providerSlug) {
      // Navigate back to provider-filtered policies
      navigate(`/admin/insurance-policies?provider=${providerSlug}`);
    } else if (location.state?.from) {
      // Navigate to the previous page if state exists
      navigate(location.state.from);
    } else if (window.history.length > 1) {
      // Go back in browser history
      navigate(-1);
    } else {
      // Fallback to all policies
      navigate('/admin/insurance-policies');
    }
  };

  const [policy, setPolicy] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedPackages, setExpandedPackages] = useState({});
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [purchaseSearchQuery, setPurchaseSearchQuery] = useState("");
  const [purchaseStatusFilter, setPurchaseStatusFilter] = useState("all");

  // Generate mock purchase data - Ready for API replacement
  const generateMockPurchases = (policySlug) => {
    const statuses = ["purchased", "purchased", "purchased", "canceled", "purchased", "canceled", "purchased"];
    const firstNames = ["John", "Jane", "Michael", "Sarah", "David", "Emily", "Robert", "Lisa", "James", "Mary"];
    const lastNames = ["Doe", "Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis", "Wilson"];
    const emails = ["john.doe@email.com", "jane.smith@email.com", "michael.j@email.com", "sarah.w@email.com", "david.b@email.com"];
    const phones = ["+233 24 123 4567", "+233 20 987 6543", "+233 26 555 1234", "+233 54 321 9876", "+233 50 111 2222"];
    const paymentMethods = ["mobile_money", "card", "mobile_money", "card", "mobile_money"];
    const transactionIds = [
      "TXN-2024-001234",
      "TXN-2024-001235",
      "TXN-2024-001236",
      "TXN-2024-001237",
      "TXN-2024-001238",
      "TXN-2024-001239",
      "TXN-2024-001240",
    ];

    return Array.from({ length: 12 }, (_, index) => {
      const status = statuses[index % statuses.length];
      const firstName = firstNames[index % firstNames.length];
      const lastName = lastNames[index % lastNames.length];
      const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@email.com`;
      const phone = phones[index % phones.length];
      const paymentMethod = paymentMethods[index % paymentMethods.length];
      const transactionId = transactionIds[index % transactionIds.length] || `TXN-2024-${String(index + 1).padStart(6, "0")}`;
      
      // Generate random dates within the last 90 days
      const daysAgo = Math.floor(Math.random() * 90);
      const purchaseDate = new Date();
      purchaseDate.setDate(purchaseDate.getDate() - daysAgo);
      
      // If canceled, cancellation date is after purchase date
      let cancellationDate = null;
      if (status === "canceled") {
        const cancelDaysAgo = Math.floor(Math.random() * daysAgo);
        cancellationDate = new Date();
        cancellationDate.setDate(cancellationDate.getDate() - cancelDaysAgo);
      }

      // Random amount between 50 and 500
      const amount = Math.floor(Math.random() * 450) + 50;

      return {
        id: `purchase-${index + 1}`,
        transaction_id: transactionId,
        customer: {
          id: `customer-${index + 1}`,
          first_name: firstName,
          last_name: lastName,
          email: email,
          phone_number: phone,
        },
        amount: amount,
        payment_method: paymentMethod,
        status: status,
        purchase_date: purchaseDate.toISOString(),
        cancellation_date: cancellationDate ? cancellationDate.toISOString() : null,
        policy_slug: policySlug,
      };
    });
  };

  // Fetch purchases - Ready for API integration
  useEffect(() => {
    const fetchPurchases = async () => {
      if (!policy_slug || activeTab !== "purchases") return;

      setPurchasesLoading(true);
      try {
        // TODO: Replace with actual API call
        // const response = await insurancePolicyService.getPolicyPurchases(policy_slug);
        // if (response?.status && response?.data?.status_code === 200) {
        //   setPurchases(response.data.data || []);
        // } else {
        //   toast.error(response?.message || "Failed to fetch purchases");
        //   setPurchases([]);
        // }

        // Mock data for now
        await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API delay
        const mockPurchases = generateMockPurchases(policy_slug);
        setPurchases(mockPurchases);
      } catch (error) {
        toast.error("An error occurred while fetching purchases");
        setPurchases([]);
      } finally {
        setPurchasesLoading(false);
      }
    };

    fetchPurchases();
  }, [policy_slug, activeTab]);

  useEffect(() => {
    const fetchPolicyDetails = async () => {
      if (!policy_slug) {
        setError("No policy identifier provided");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await insurancePolicyService.getInsurancePolicy(policy_slug);
        
        if (response?.data.status_code === 200 && response?.data?.data) {
          const policyData = response.data.data;
          
          
          if (Array.isArray(policyData.insurance_provider) && policyData.insurance_provider.length > 0) {
            policyData.insurance_provider = policyData.insurance_provider[0];
          } else if (!policyData.insurance_provider) {
            policyData.insurance_provider = null;
          }
          
          setPolicy(policyData);
        } else {
          const errorMessage = response?.message || "Failed to fetch policy details";
          toast.error(errorMessage);
          setError(errorMessage);
          setPolicy(null);
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.data?.reason || error?.message || "Failed to fetch policy details";
        toast.error(errorMessage);
        setError(errorMessage);
        setPolicy(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPolicyDetails();
  }, [policy_slug]);

  const handleApprove = async (policyId) => {
    try {
      const response = await insurancePolicyService.approvePolicy(policyId);
      if (response.status) {
        setPolicy(prev => ({
          ...prev,
          status: "approved",
          approved_date: new Date().toISOString()
        }));
        toast.success(response.message || "Policy approved successfully!");
      } else {
        toast.error(response.message || "Failed to approve policy");
      }
    } catch (error) {
      toast.error("An error occurred while approving the policy");
    }
  };

  const handleReject = async (policyId, reason) => {
    try {
      const response = await insurancePolicyService.rejectPolicy(policyId, reason);
      if (response.status) {
        setPolicy(prev => ({
          ...prev,
          status: "rejected",
          rejected_date: new Date().toISOString(),
          rejection_reason: reason
        }));
        toast.success(response.message || "Policy rejected successfully!");
      } else {
        toast.error(response.message || "Failed to reject policy");
      }
    } catch (error) {
      toast.error("An error occurred while rejecting the policy");
    }
  };

  const getPolicyTypeInfo = (policyType, shortDescription, insuranceType) => {
    const insuranceTypeValue = shortDescription || insuranceType || "";
    
    if (insuranceTypeValue === "life_insurance" || policyType === "life_insurance") {
      return {
        icon: Heart,
        label: "Life Insurance",
        color: "blue",
        bgGradient: "from-blue-500 to-indigo-600",
      };
    }
    
    if (insuranceTypeValue === "general_insurance") {
      const subcategoryMap = {
        health_insurance: {
          icon: Users,
          label: "Health Insurance",
          color: "pink",
          bgGradient: "from-pink-500 to-rose-600",
        },
        home_insurance: {
          icon: Home,
          label: "Home Insurance",
          color: "purple",
          bgGradient: "from-purple-500 to-violet-600",
        },
        accident_insurance: {
          icon: AlertTriangle,
          label: "Accident Insurance",
          color: "red",
          bgGradient: "from-red-500 to-rose-600",
        },
      };
      
      if (subcategoryMap[policyType]) {
        return subcategoryMap[policyType];
      }
      
      return {
        icon: Shield,
        label: "General Insurance",
        color: "indigo",
        bgGradient: "from-indigo-500 to-purple-600",
      };
    }
    
    const travelTypeMap = {
      daily_commuter: {
        icon: Bus,
        label: "Daily Commuter",
        color: "blue",
        bgGradient: "from-blue-500 to-indigo-600",
      },
      intercity_travel: {
        icon: Car,
        label: "Intercity Travel",
        color: "green",
        bgGradient: "from-green-500 to-emerald-600",
      },
      okada_rider: {
        icon: Zap,
        label: "Motor Rider",
        color: "purple",
        bgGradient: "from-purple-500 to-pink-600",
      },
    };

    return (
      travelTypeMap[policyType] || {
        icon: Shield,
        label: "General Policy",
        color: "gray",
        bgGradient: "from-gray-500 to-gray-600",
      }
    );
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      approved: { color: "green", label: "Approved", icon: CheckCircle },
      pending: { color: "yellow", label: "Pending Review", icon: Clock },
      rejected: { color: "red", label: "Rejected", icon: XCircle },
    };

    return statusMap[status] || statusMap.pending;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Filter purchases based on search and status
  const filteredPurchases = purchases.filter((purchase) => {
    const matchesSearch =
      purchase.customer?.first_name?.toLowerCase().includes(purchaseSearchQuery.toLowerCase()) ||
      purchase.customer?.last_name?.toLowerCase().includes(purchaseSearchQuery.toLowerCase()) ||
      purchase.customer?.email?.toLowerCase().includes(purchaseSearchQuery.toLowerCase()) ||
      purchase.customer?.phone_number?.includes(purchaseSearchQuery) ||
      purchase.transaction_id?.toLowerCase().includes(purchaseSearchQuery.toLowerCase());
    
    const matchesStatus = purchaseStatusFilter === "all" || purchase.status === purchaseStatusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Calculate purchase statistics
  const purchaseStats = {
    total: purchases.length,
    purchased: purchases.filter((p) => p.status === "purchased").length,
    canceled: purchases.filter((p) => p.status === "canceled").length,
    totalRevenue: purchases
      .filter((p) => p.status === "purchased")
      .reduce((sum, p) => sum + (p.amount || 0), 0),
  };

  const togglePackage = (index) => {
    setExpandedPackages((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: Info },
    { id: "coverage", label: "Coverage", icon: Shield },
    { id: "claims", label: "Claims Process", icon: FileText },
    { id: "purchases", label: "Purchases", icon: ShoppingCart },
  ];

  if (isLoading) {
    return (
      <Loader
        fullScreen={true}
        text="Loading policy details..."
        subText="Please wait while we fetch the policy information"
      />
    );
  }

  if (error || !policy) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-8 mx-auto max-w-md text-center bg-white rounded-2xl shadow-xl"
        >
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-red-100 rounded-full">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <h2 className="mb-2 text-2xl font-bold text-gray-900">
            Failed to Load Policy
          </h2>
          <p className="mb-6 text-gray-600">{error || "Policy not found"}</p>
          <div className="flex justify-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBack}
              className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Policies
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  const insuranceType = policy.short_description || policy.insurance_type || "";
  const isTravelInsurance = insuranceType === "travel_insurance";
  const isLifeInsurance = insuranceType === "life_insurance";
  const isGeneralInsurance = insuranceType === "general_insurance";

  const coverageDurations = Array.isArray(policy.coverage_durations) 
    ? policy.coverage_durations 
    : [];

  const packages = (isLifeInsurance || isGeneralInsurance) 
    ? coverageDurations.map((pkg, idx) => ({
        ...pkg,
        id: idx,
      }))
    : [];

  const travelPackages = (isTravelInsurance && policy.policy_type === "travel_insurance")
    ? coverageDurations.map((pkg, idx) => ({
        ...pkg,
        id: idx,
      }))
    : [];

  const getPrice = (item) => {
    if (item && typeof item === "object") {
      if (item.price !== undefined) return item.price;
      if (item.premium !== undefined) return item.premium;
    }
    return 0;
  };

  const getPriceRange = () => {
    if (coverageDurations.length === 0) return { min: 0, max: 0 };
    const prices = coverageDurations.map(d => getPrice(d)).filter(p => p > 0);
    if (prices.length === 0) return { min: 0, max: 0 };
    return { min: Math.min(...prices), max: Math.max(...prices) };
  };

  const priceRange = getPriceRange();
  const typeInfo = getPolicyTypeInfo(policy.policy_type, policy.short_description, policy.insurance_type);
  const statusInfo = getStatusInfo(policy.status || "pending");
  const TypeIcon = typeInfo.icon;
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50">
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          {/* Top Bar - Back Button and Actions */}
          <div className="flex justify-between items-center mb-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBack}
              className="flex gap-2 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Policies
            </motion.button>

            {/* Actions Dropdown */}
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
              >
                Actions
                <ChevronDown className={`w-4 h-4 transition-transform ${showActionsDropdown ? 'rotate-180' : ''}`} />
              </motion.button>

              <AnimatePresence>
                {showActionsDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowActionsDropdown(false)}
                    />
                    
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 z-20 mt-2 w-48 bg-white rounded-lg border border-gray-200 shadow-xl"
                    >
                      <div className="py-1">
                        {/* Show Approve option when status is pending or rejected */}
                        {(policy.status === "pending" || policy.status === "rejected") && (
                          <button
                            onClick={() => {
                              setShowActionsDropdown(false);
                              setShowApprovalModal(true);
                            }}
                            className="flex items-center gap-3 px-4 py-2.5 w-full text-left text-sm text-gray-700 transition-colors hover:bg-green-50"
                          >
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <div>
                              <p className="font-medium text-green-700">Approve Policy</p>
                              <p className="text-xs text-gray-500">Make policy live</p>
                            </div>
                          </button>
                        )}
                        
                        {/* Show divider only when both options are visible (pending status) */}
                        {policy.status === "pending" && (
                          <div className="my-1 border-t border-gray-100" />
                        )}
                        
                        {/* Show Reject option when status is pending or approved */}
                        {(policy.status === "pending" || policy.status === "approved") && (
                          <button
                            onClick={() => {
                              setShowActionsDropdown(false);
                              setShowRejectionModal(true);
                            }}
                            className="flex items-center gap-3 px-4 py-2.5 w-full text-left text-sm text-gray-700 transition-colors hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 text-red-600" />
                            <div>
                              <p className="font-medium text-red-700">Reject Policy</p>
                              <p className="text-xs text-gray-500">Decline with reason</p>
                            </div>
                          </button>
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Policy Title and Info */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
            <div
              className={`p-3 flex items-center justify-center bg-gradient-to-r ${typeInfo.bgGradient} rounded-xl flex-shrink-0`}
            >
              <TypeIcon className="w-8 h-8 text-white" />
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="mb-2 text-2xl font-bold text-gray-900 break-words sm:text-3xl lg:text-4xl">
                {policy.policy_name}
              </h1>
              <div className="flex flex-wrap gap-2 items-center mb-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${typeInfo.color}-100 text-${typeInfo.color}-700`}
                >
                  {typeInfo.label}
                </span>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-700`}
                >
                  <StatusIcon className="mr-1.5 w-4 h-4" />
                  {statusInfo.label}
                </span>
              </div>
              <p className="text-base leading-relaxed text-gray-600">
                {policy.description}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Provider Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-4 mb-6 bg-white rounded-xl border border-gray-200 shadow-sm sm:p-6"
        >
          <h3 className="flex gap-2 items-center mb-4 text-base font-semibold text-gray-900">
            <Building className="w-5 h-5 text-gray-700" />
            Provider Information
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-xs text-gray-500">Provider Name</p>
              <p className="font-medium text-gray-900">
                {policy.insurance_provider?.company_name || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Contact Email</p>
              <p className="flex gap-2 items-center font-medium text-gray-900">
                <Mail className="w-4 h-4 text-gray-400" />
                {policy.insurance_provider?.email || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Phone Number</p>
              <p className="flex gap-2 items-center font-medium text-gray-900">
                <Calendar className="w-4 h-4 text-gray-400" />
                {policy.insurance_provider?.phone_number || "N/A"}
              </p>
            </div>
          </div>
          
          {/* Provider Logo */}
          {policy.insurance_provider?.company_logo && (
            <div className="pt-4 mt-4 border-t border-gray-200">
              <p className="mb-2 text-xs text-gray-500">Company Logo</p>
              <div className="overflow-hidden w-24 h-24 bg-white rounded-lg border-2 border-gray-200 shadow-sm">
                <img
                  src={policy.insurance_provider.company_logo}
                  alt={`${policy.insurance_provider.company_name} logo`}
                  className="object-cover w-full h-full"
                />
              </div>
            </div>
          )}

          {/* Rejection Reason */}
          {policy.status === "rejected" && policy.rejection_reason && (
            <div className="flex gap-3 items-start p-4 mt-4 bg-red-50 rounded-lg border border-red-200">
              <AlertTriangle className="flex-shrink-0 w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-900">Rejection Reason:</p>
                <p className="mt-1 text-sm text-red-700">{policy.rejection_reason}</p>
                {policy.rejected_date && (
                  <p className="mt-2 text-xs text-red-600">
                    Rejected on {formatDate(policy.rejected_date)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Approval Info */}
          {policy.status === "approved" && policy.approved_date && (
            <div className="flex gap-3 items-start p-4 mt-4 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle className="flex-shrink-0 w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">Policy Approved</p>
                <p className="mt-1 text-sm text-green-700">
                  This policy was approved on {formatDate(policy.approved_date)}
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6 sm:mb-8"
        >
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
            <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow sm:p-4 hover:shadow-md">
              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                <div className="flex-shrink-0 self-start p-2 bg-emerald-100 rounded-lg">
                  <Banknote className="w-4 h-4 text-emerald-600 sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="mb-1 text-xs text-gray-500">Price Range</p>
                  <p className="text-xs font-bold leading-tight text-gray-900 sm:text-sm lg:text-base">
                    {priceRange.min === priceRange.max 
                      ? formatCurrency(priceRange.min)
                      : `${formatCurrency(priceRange.min)} - ${formatCurrency(priceRange.max)}`
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow sm:p-4 hover:shadow-md">
              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                <div className="flex-shrink-0 self-start p-2 bg-blue-100 rounded-lg">
                  <Target className="w-4 h-4 text-blue-600 sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="mb-1 text-xs text-gray-500">Max Claims</p>
                  <p className="text-xs font-bold text-gray-900 sm:text-sm lg:text-base">
                    {policy.max_claims}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow sm:p-4 hover:shadow-md">
              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                <div className="flex-shrink-0 self-start p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-4 h-4 text-purple-600 sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="mb-1 text-xs text-gray-500">Waiting Period</p>
                  <p className="text-xs font-bold text-gray-900 sm:text-sm lg:text-base">
                    {policy.waiting_period} days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6 sm:mb-8"
        >
          <div className="bg-white rounded-t-xl border-b border-gray-200">
            <nav
              className="flex overflow-x-auto px-2 scrollbar-hide sm:px-4 lg:px-6"
              aria-label="Tabs"
            >
              <div className="flex space-x-2 min-w-max sm:space-x-4 lg:space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;

                  return (
                    <motion.button
                      key={tab.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center py-3 sm:py-4 px-2 sm:px-3 lg:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                        isActive
                          ? "text-blue-600 border-blue-500"
                          : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <Icon className="flex-shrink-0 mr-1 w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                      {tab.label}
                    </motion.button>
                  );
                })}
              </div>
            </nav>
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-t-none rounded-b-xl border border-gray-200 shadow-sm"
        >
          {activeTab === "overview" && (
            <div className="p-4 space-y-6 sm:p-6">
              {/* Policy Description */}
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h3 className="flex gap-2 items-center mb-3 text-base font-semibold text-gray-900">
                  <Info className="w-4 h-4 text-gray-700" />
                  Policy Description
                </h3>
                <p className="text-sm leading-relaxed text-gray-700">
                  {policy.description}
                </p>
              </div>

              {/* Policy Details Grid */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Policy Information */}
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="mb-4 text-sm font-semibold text-gray-900">
                    Policy Information
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-start py-2 border-b border-gray-100">
                      <div className="flex gap-2 items-center">
                        <Target className="flex-shrink-0 w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Max Claims</span>
                      </div>
                      <span className="text-sm font-medium text-right text-gray-900">
                        {policy.max_claims}
                      </span>
                    </div>

                    <div className="flex justify-between items-start py-2 border-b border-gray-100">
                      <div className="flex gap-2 items-center">
                        <Clock className="flex-shrink-0 w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Waiting Period</span>
                      </div>
                      <span className="text-sm font-medium text-right text-gray-900">
                        {policy.waiting_period} days
                      </span>
                    </div>

                    <div className="flex justify-between items-start py-2 border-b border-gray-100">
                      <div className="flex gap-2 items-center">
                        <Shield className="flex-shrink-0 w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Insurance Type</span>
                      </div>
                      <span className="text-sm font-medium text-right text-gray-900 capitalize">
                        {typeInfo.label}
                      </span>
                    </div>

                    <div className="flex justify-between items-start py-2">
                      <div className="flex gap-2 items-center">
                        <MapPin className="flex-shrink-0 w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Geography</span>
                      </div>
                      <span className="text-sm font-medium text-right text-gray-900 capitalize">
                        {policy.geography}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Eligibility Criteria */}
                {(() => {
                  const relevantPackages = isTravelInsurance ? travelPackages : packages;
                  const hasEligibility = relevantPackages.length > 0 && relevantPackages[0]?.eligibility_criteria?.length > 0;
                  
                  if ((isLifeInsurance || isGeneralInsurance || isTravelInsurance) && hasEligibility) {
                    return (
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <div className="flex gap-2 items-center mb-3">
                          <CheckCircle className="w-4 h-4 text-gray-700" />
                          <h4 className="text-sm font-semibold text-gray-900">Eligibility Criteria</h4>
                        </div>
                        <div className="space-y-2">
                          {relevantPackages[0].eligibility_criteria.map((criteria, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{criteria}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  } else if (policy.eligibility_criteria?.length > 0) {
                    return (
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <div className="flex gap-2 items-center mb-3">
                          <CheckCircle className="w-4 h-4 text-gray-700" />
                          <h4 className="text-sm font-semibold text-gray-900">Eligibility Criteria</h4>
                        </div>
                        <div className="space-y-2">
                          {policy.eligibility_criteria.map((criteria, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{criteria}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Exclusions */}
                {(() => {
                  const relevantPackages = isTravelInsurance ? travelPackages : packages;
                  const hasExclusions = relevantPackages.length > 0 && relevantPackages[0]?.exclusions?.length > 0;
                  
                  if ((isLifeInsurance || isGeneralInsurance || isTravelInsurance) && hasExclusions) {
                    return (
                      <div className="p-4 bg-white rounded-lg border border-gray-200 sm:col-span-2">
                        <div className="flex gap-2 items-center mb-3">
                          <XCircle className="w-4 h-4 text-gray-700" />
                          <h4 className="text-sm font-semibold text-gray-900">Exclusions</h4>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {relevantPackages[0].exclusions.map((exclusion, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{exclusion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  } else if (policy.exclusions?.length > 0) {
                    return (
                      <div className="p-4 bg-white rounded-lg border border-gray-200 sm:col-span-2">
                        <div className="flex gap-2 items-center mb-3">
                          <XCircle className="w-4 h-4 text-gray-700" />
                          <h4 className="text-sm font-semibold text-gray-900">Exclusions</h4>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {policy.exclusions.map((exclusion, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{exclusion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}

          {activeTab === "coverage" && (
            <div className="p-4 space-y-6 sm:p-6">
              {/* Coverage content - simplified for brevity, follows same pattern as provider */}
              {/* Intercity Travel Routes */}
              {policy.policy_type === "intercity_travel" && (
                <div className="space-y-6">
                  {coverageDurations.some(d => d.route_type === "regional") && (
                    <div>
                      <h3 className="flex items-center mb-4 text-base font-semibold text-gray-900 sm:text-lg">
                        <MapPin className="mr-2 w-4 h-4 text-blue-500 sm:w-5 sm:h-5" />
                        Regional Routes
                      </h3>
                      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {coverageDurations
                          .filter(d => d.route_type === "regional")
                          .map((route, index) => (
                            <div
                              key={index}
                              className="relative p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200"
                            >
                              <div className="mb-3">
                                <p className="mb-1 text-xs text-gray-600">Route</p>
                                <p className="text-sm font-bold text-gray-900">
                                  Greater Accra → {route.route_destination}
                                </p>
                              </div>
                              <div className="flex justify-between items-center pt-3 border-t border-blue-200">
                                <div>
                                  <p className="text-xs text-gray-600">Duration</p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {route.display_value} {route.display_unit}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-600">Price</p>
                                  <p className="text-lg font-extrabold text-blue-600">
                                    {formatCurrency(route.price)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Life/General/Travel Insurance Packages */}
              {(isLifeInsurance || isGeneralInsurance || (isTravelInsurance && policy.policy_type === "travel_insurance")) && packages.length > 0 && (
                <div>
                  <h3 className="flex items-center mb-4 text-base font-semibold text-gray-900 sm:text-lg">
                    <Shield className="mr-2 w-4 h-4 text-blue-600 sm:w-5 sm:h-5" />
                    Coverage Packages
                  </h3>
                  <div className="space-y-3">
                    {packages.map((pkg, index) => {
                      const isExpanded = expandedPackages[index];
                      return (
                        <div
                          key={index}
                          className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm"
                        >
                          <button
                            onClick={() => togglePackage(index)}
                            className="flex justify-between items-center px-4 py-4 w-full text-left hover:bg-gray-50"
                          >
                            <div className="flex-1">
                              <h4 className="text-base font-semibold text-gray-900">
                                {pkg.product_name || `Package ${index + 1}`}
                              </h4>
                              <p className="text-sm text-gray-600">{pkg.description}</p>
                            </div>
                            <div className="flex gap-4 items-center ml-4">
                              {pkg.duration && (
                                <div>
                                  <p className="text-xs text-gray-500">Duration</p>
                                  <p className="text-sm font-medium text-gray-900">{pkg.duration}</p>
                                </div>
                              )}
                              <div>
                                <p className="text-xs text-gray-500">Premium</p>
                                <p className="text-lg font-bold text-gray-900">
                                  {formatCurrency(pkg.premium || 0)}
                                </p>
                                {pkg.premium_duration && (
                                  <p className="text-xs text-gray-500 capitalize mt-0.5">
                                    per {pkg.premium_duration}
                                  </p>
                                )}
                              </div>
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </button>

                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="border-t border-gray-200"
                              >
                                <div className="grid grid-cols-1 gap-4 p-4 bg-gray-50 sm:grid-cols-2">
                                  {/* Package Details */}
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <h5 className="mb-3 text-sm font-semibold text-gray-900">Package Details</h5>
                                    <div className="space-y-2">
                                      {pkg.duration && (
                                        <div className="flex justify-between items-start py-2 border-b border-gray-100">
                                          <span className="text-xs text-gray-600">Duration</span>
                                          <span className="text-sm font-medium text-gray-900">{pkg.duration}</span>
                                        </div>
                                      )}
                                      {pkg.premium_duration && (
                                        <div className="flex justify-between items-start py-2 border-b border-gray-100">
                                          <span className="text-xs text-gray-600">Premium Duration</span>
                                          <span className="text-sm font-medium text-gray-900 capitalize">{pkg.premium_duration}</span>
                                        </div>
                                      )}
                                      {pkg.premium && (
                                        <div className="flex justify-between items-start py-2">
                                          <span className="text-xs text-gray-600">Premium</span>
                                          <span className="text-sm font-bold text-gray-900">{formatCurrency(pkg.premium)}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Coverage Benefits */}
                                  {pkg.insurance_coverage_list?.length > 0 && (
                                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                                      <div className="flex gap-2 items-center mb-3">
                                        <Shield className="w-4 h-4 text-gray-700" />
                                        <h5 className="text-sm font-semibold text-gray-900">Coverage Benefits</h5>
                                      </div>
                                      <div className="space-y-2">
                                        {pkg.insurance_coverage_list.map((benefit, i) => (
                                          <div key={i} className="flex justify-between items-start">
                                            <div className="flex-1">
                                              <div className="flex gap-2 items-start">
                                                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                                <div className="flex-1">
                                                  <span className="text-sm font-medium text-gray-900">{benefit.title}</span>
                                                  {benefit.description && (
                                                    <p className="text-xs text-gray-600 mt-0.5">{benefit.description}</p>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            {benefit.limit && (
                                              <span className="ml-2 text-sm font-semibold text-gray-900 whitespace-nowrap">
                                                {formatCurrency(Number(benefit.limit) || 0)}
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Exclusions */}
                                  {pkg.exclusions?.length > 0 && (
                                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                                      <div className="flex gap-2 items-center mb-3">
                                        <XCircle className="w-4 h-4 text-gray-700" />
                                        <h5 className="text-sm font-semibold text-gray-900">Exclusions</h5>
                                      </div>
                                      <div className="space-y-2">
                                        {pkg.exclusions.map((exclusion, i) => (
                                          <div key={i} className="flex gap-2 items-start">
                                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                            <span className="text-sm text-gray-700">{exclusion}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Eligibility Criteria */}
                                  {pkg.eligibility_criteria?.length > 0 && (
                                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                                      <div className="flex gap-2 items-center mb-3">
                                        <CheckCircle className="w-4 h-4 text-gray-700" />
                                        <h5 className="text-sm font-semibold text-gray-900">Eligibility Criteria</h5>
                                      </div>
                                      <div className="space-y-2">
                                        {pkg.eligibility_criteria.map((criteria, i) => (
                                          <div key={i} className="flex gap-2 items-start">
                                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                            <span className="text-sm text-gray-700">{criteria}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Claim Process Steps */}
                                  {pkg.claim_process_steps?.length > 0 && (
                                    <div className="p-4 bg-white rounded-lg border border-gray-200 sm:col-span-2">
                                      <div className="flex gap-2 items-center mb-3">
                                        <FileText className="w-4 h-4 text-gray-700" />
                                        <h5 className="text-sm font-semibold text-gray-900">Claim Process</h5>
                                      </div>
                                      <div className="space-y-3">
                                        {pkg.claim_process_steps.map((step, i) => (
                                          <div key={step.id || i} className="flex gap-3 items-start">
                                            <div className="flex flex-shrink-0 justify-center items-center w-6 h-6 text-xs font-bold text-gray-700 bg-gray-200 rounded-full">
                                              {step.step_number || i + 1}
                                            </div>
                                            <div className="flex-1">
                                              <p className="text-sm font-medium text-gray-900">{step.title}</p>
                                              {step.description && (
                                                <p className="mt-1 text-xs text-gray-600">{step.description}</p>
                                              )}
                                              <div className="flex flex-wrap gap-3 mt-2">
                                                {step.timeframe && (
                                                  <div className="flex gap-1 items-center">
                                                    <Clock className="w-3 h-3 text-gray-400" />
                                                    <span className="text-xs text-gray-500">Timeframe: {step.timeframe}</span>
                                                  </div>
                                                )}
                                                {step.requirements && (
                                                  <div className="flex gap-1 items-center">
                                                    <FileText className="w-3 h-3 text-gray-400" />
                                                    <span className="text-xs text-gray-500">Requirements: {step.requirements}</span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Terms & Conditions */}
                                  {pkg.terms && (
                                    <div className="p-4 bg-white rounded-lg border border-gray-200 sm:col-span-2">
                                      <div className="flex gap-2 items-center mb-3">
                                        <FileText className="w-4 h-4 text-gray-700" />
                                        <h5 className="text-sm font-semibold text-gray-900">Terms & Conditions</h5>
                                      </div>
                                      <div className="text-sm text-gray-700">
                                        {String(pkg.terms).startsWith("http") ? (
                                          <a
                                            href={pkg.terms}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-600 underline break-all hover:text-blue-700"
                                          >
                                            View Terms & Conditions Document
                                          </a>
                                        ) : (
                                          <div className="overflow-y-auto pr-2 max-h-48 whitespace-pre-wrap break-words">
                                            {pkg.terms}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Insurance Coverage List for general policies */}
              {policy.insurance_coverage_list?.length > 0 && (
                <div>
                  <h3 className="flex gap-2 items-center mb-4 text-base font-semibold text-gray-900">
                    <Shield className="w-4 h-4 text-gray-700" />
                    Insurance Coverage Details
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {policy.insurance_coverage_list.map((coverage, index) => (
                      <div
                        key={index}
                        className="p-4 bg-white rounded-lg border border-gray-200"
                      >
                        <h4 className="mb-2 text-sm font-semibold text-gray-900">
                          {coverage.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {coverage.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "claims" && (
            <div className="p-4 space-y-6 sm:p-6">
              {/* Claims process - follows same pattern as provider */}
              {policy.claim_process_steps?.length > 0 && (
                <div className="space-y-4">
                  {policy.claim_process_steps.map((step, index) => (
                    <div key={index} className="relative">
                      {index < policy.claim_process_steps.length - 1 && (
                        <div className="absolute left-5 top-12 w-0.5 h-full bg-gray-200" />
                      )}
                      <div className="flex gap-4 items-start">
                        <div className="flex-shrink-0">
                          <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-full">
                            <span className="text-sm font-bold text-blue-700">
                              {step.step_number || index + 1}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 pb-6">
                          <h4 className="mb-2 text-base font-semibold text-gray-900">
                            {step.title}
                          </h4>
                          <p className="mb-3 text-sm text-gray-600">
                            {step.description}
                          </p>
                          {step.timeframe && (
                            <div className="flex gap-2 items-center">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <p className="text-xs text-gray-500">{step.timeframe}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Package-based claims for Life/General Insurance */}
              {(isLifeInsurance || isGeneralInsurance) && packages.length > 0 && packages[0]?.claim_process_steps?.length > 0 && (
                <div className="space-y-6">
                  {packages.map((pkg, pkgIndex) => {
                    if (!pkg.claim_process_steps?.length) return null;
                    return (
                      <div key={pkgIndex} className="p-4 bg-white rounded-lg border border-gray-200">
                        <h3 className="mb-4 text-base font-semibold text-gray-900">
                          {pkg.product_name || `Package ${pkgIndex + 1}`} - Claims Process
                        </h3>
                        <div className="space-y-4">
                          {pkg.claim_process_steps.map((step, stepIndex) => (
                            <div key={stepIndex} className="relative">
                              {stepIndex < pkg.claim_process_steps.length - 1 && (
                                <div className="absolute left-5 top-12 w-0.5 h-full bg-gray-200" />
                              )}
                              <div className="flex gap-4 items-start">
                                <div className="flex-shrink-0">
                                  <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-full">
                                    <span className="text-sm font-bold text-blue-700">
                                      {step.step_number || stepIndex + 1}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex-1 pb-6">
                                  <h4 className="mb-2 text-base font-semibold text-gray-900">
                                    {step.title}
                                  </h4>
                                  <p className="mb-3 text-sm text-gray-600">
                                    {step.description}
                                  </p>
                                  {step.timeframe && (
                                    <div className="flex gap-2 items-center">
                                      <Clock className="w-4 h-4 text-gray-400" />
                                      <p className="text-xs text-gray-500">{step.timeframe}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === "purchases" && (
            <div className="p-4 space-y-6 sm:p-6">
              {/* Statistics Cards */}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500">Total Purchases</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">{purchaseStats.total}</p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <ShoppingCart className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500">Active</p>
                      <p className="mt-1 text-2xl font-bold text-green-600">{purchaseStats.purchased}</p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500">Canceled</p>
                      <p className="mt-1 text-2xl font-bold text-red-600">{purchaseStats.canceled}</p>
                    </div>
                    <div className="p-2 bg-red-100 rounded-lg">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500">Total Revenue</p>
                      <p className="mt-1 text-xl font-bold text-emerald-600">{formatCurrency(purchaseStats.totalRevenue)}</p>
                    </div>
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Banknote className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search by name, email, phone, or transaction ID..."
                      value={purchaseSearchQuery}
                      onChange={(e) => setPurchaseSearchQuery(e.target.value)}
                      className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Status Filter */}
                  <div className="relative">
                    <Filter className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
                    <select
                      value={purchaseStatusFilter}
                      onChange={(e) => setPurchaseStatusFilter(e.target.value)}
                      className="py-2 pr-10 pl-10 w-full rounded-lg border border-gray-300 transition-colors appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Status</option>
                      <option value="purchased">Purchased</option>
                      <option value="canceled">Canceled</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Purchases Table */}
              {purchasesLoading ? (
                <div className="flex justify-center items-center p-12 bg-white rounded-xl border border-gray-200">
                  <div className="text-center">
                    <div className="w-8 h-8 mx-auto mb-4 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
                    <p className="text-sm text-gray-600">Loading purchases...</p>
                  </div>
                </div>
              ) : filteredPurchases.length === 0 ? (
                <div className="p-12 text-center bg-white rounded-xl border-2 border-gray-300 border-dashed">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 bg-gray-100 rounded-full">
                      <ShoppingCart className="w-16 h-16 text-gray-400" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-gray-900">No Purchases Found</h3>
                  <p className="text-gray-600">
                    {purchaseSearchQuery || purchaseStatusFilter !== "all"
                      ? "No purchases match your current filters. Try adjusting your search or filter criteria."
                      : "No purchases have been made for this policy yet."}
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                            Customer
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                            Transaction ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                            Payment Method
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                            Purchase Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredPurchases.map((purchase) => (
                          <tr key={purchase.id} className="transition-colors hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 w-10 h-10">
                                  <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full">
                                    <User className="w-5 h-5 text-white" />
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {purchase.customer?.first_name} {purchase.customer?.last_name}
                                  </div>
                                  <div className="text-sm text-gray-500 flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {purchase.customer?.email}
                                  </div>
                                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                    <Phone className="w-3 h-3" />
                                    {purchase.customer?.phone_number}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                              <div className="text-sm font-mono text-gray-900">{purchase.transaction_id}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                              <div className="text-sm font-semibold text-gray-900">{formatCurrency(purchase.amount)}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-900 capitalize">
                                  {purchase.payment_method?.replace(/_/g, " ") || "N/A"}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                              <div className="text-sm text-gray-900">{formatDateTime(purchase.purchase_date)}</div>
                              {purchase.cancellation_date && (
                                <div className="text-xs text-red-600 mt-1">
                                  Canceled: {formatDateTime(purchase.cancellation_date)}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  purchase.status === "purchased"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {purchase.status === "purchased" ? (
                                  <>
                                    <CheckCircle className="mr-1 w-3 h-3" />
                                    Purchased
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="mr-1 w-3 h-3" />
                                    Canceled
                                  </>
                                )}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>

      {/* Modals */}
      <ApprovalModal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        policy={policy}
        onApprove={handleApprove}
      />
      <RejectionModal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        policy={policy}
        onReject={handleReject}
      />

      {/* Custom CSS */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
